/**
 * ReorderPolicyAdminPanel — admin-only widget that surfaces the four
 * product-family reorder minimums (Vinyl / CD / DVD / Blu-ray) so the
 * team can edit them without touching the DB.
 *
 * Compact by design: renders inline on the Catalogue Library page so
 * admins see + edit the defaults right next to the catalogue entries
 * whose minimums those defaults control. Per-entry overrides still live
 * on the catalogue-entry edit form (``min_reorder_quantity``).
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { catalogueApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Loader2, Save, RefreshCw } from 'lucide-react';

const FAMILIES = [
    { key: 'vinyl',  label: 'Vinyl',    accent: 'text-violet-600' },
    { key: 'cd',     label: 'CD',       accent: 'text-cyan-600' },
    { key: 'dvd',    label: 'DVD',      accent: 'text-amber-600' },
    { key: 'bluray', label: 'Blu-ray',  accent: 'text-blue-600' },
];

export function ReorderPolicyAdminPanel() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [values, setValues] = useState({});   // {family: "250"}
    const [initial, setInitial] = useState({}); // for dirty check

    const load = async () => {
        setLoading(true);
        try {
            const res = await catalogueApi.listReorderPolicies();
            const map = {};
            (res.data.defaults || []).forEach((d) => {
                map[d.product_family] = String(d.min_qty);
            });
            setValues(map);
            setInitial(map);
        } catch {
            toast.error('Failed to load reorder policies');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const dirty = FAMILIES.some((f) => values[f.key] !== initial[f.key]);

    const save = async () => {
        // Only send the families that actually changed.
        const policies = FAMILIES
            .filter((f) => values[f.key] !== initial[f.key])
            .map((f) => ({
                product_family: f.key,
                min_qty: parseInt(values[f.key], 10),
            }))
            .filter((p) => Number.isFinite(p.min_qty) && p.min_qty >= 1);
        if (policies.length === 0) return;
        setSaving(true);
        try {
            await catalogueApi.updateReorderPolicies(policies);
            toast.success('Reorder minimums updated');
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card className="mb-6 border-slate-200" data-testid="reorder-policy-admin-panel">
            <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                    <RefreshCw className="h-4 w-4 text-slate-500" />
                    Minimum reorder quantity per product family
                </CardTitle>
                <p className="text-[11px] text-slate-500 mt-1">
                    Applies to every catalogue entry unless the entry has its own
                    <span className="mx-1 font-medium">Minimum reorder quantity</span>
                    override.
                </p>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
                        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {FAMILIES.map((f) => (
                            <div key={f.key} className="space-y-1.5">
                                <Label className={`text-xs font-medium ${f.accent}`}>{f.label}</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={values[f.key] ?? ''}
                                    onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                                    data-testid={`reorder-policy-${f.key}`}
                                />
                            </div>
                        ))}
                    </div>
                )}
                <div className="flex justify-end mt-4">
                    <Button
                        size="sm"
                        disabled={!dirty || saving || loading}
                        onClick={save}
                        data-testid="reorder-policy-save"
                    >
                        {saving
                            ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Saving…</>
                            : <><Save className="h-3.5 w-3.5 mr-1" /> Save minimums</>}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}
